import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { FeatureKey, SubscriptionPlan, FEATURES } from '../utils/features';
import { useAuth } from './AuthContext';
import { auth } from '../utils/firebase';

// Initialize Stripe - handle missing environment variable gracefully
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string;
console.log('🔑 Stripe key available:', !!stripeKey);

const stripePromise = stripeKey ? loadStripe(stripeKey) : Promise.resolve(null);
console.log('⚡ Stripe promise initialized');

// Helper to get Firebase ID token
const getAuthToken = async (): Promise<string | null> => {
  const currentUser = auth.currentUser;
  if (!currentUser) return null;
  
  try {
    return await currentUser.getIdToken();
  } catch (error) {
    console.error('❌ Failed to get auth token:', error);
    return null;
  }
};

// Subscription state interface
export interface SubscriptionState {
  plan: SubscriptionPlan;
  isPremium: boolean;
  isLoading: boolean;
  customerId?: string;
  subscriptionId?: string;
  expiresAt?: Date;
}

// Billing period type
export type BillingPeriod = 'monthly' | 'yearly';

// Context interface
interface SubscriptionContextType {
  // State
  subscription: SubscriptionState;
  
  // Feature access
  hasFeature: (feature: FeatureKey) => boolean;
  requiresPremium: (feature: FeatureKey) => boolean;
  
  // Actions
  upgradeToPremium: (triggerFeature?: FeatureKey, billingPeriod?: BillingPeriod) => void;
  cancelSubscription: () => void;
  
  // Development helpers
  setDevMode: (isPremium: boolean) => void;
  
  // Modal controls
  setUpgradeModalTrigger: (trigger: ((feature?: FeatureKey, billingPeriod?: BillingPeriod) => void) | null) => void;
}

// Create context
const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

// Provider props
interface SubscriptionProviderProps {
  children: ReactNode;
}

// Development mode helper - only works in development
const getDevPremiumState = (): boolean => {
  // Disable dev premium in production for security
  if (import.meta.env.PROD) return false;
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('DEV_FORCE_PREMIUM') === 'true';
};

// Default state - check localStorage immediately for faster premium activation
const getInitialState = (): SubscriptionState => {
  const devPremium = getDevPremiumState();
  if (devPremium) {
    return {
      plan: 'premium',
      isPremium: true,
      isLoading: false,
      customerId: 'dev_customer',
      subscriptionId: 'dev_subscription'
    };
  }
  return {
    plan: 'free',
    isPremium: false,
    isLoading: true
  };
};

// Provider component
export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  console.log('🏗️ SubscriptionProvider: Starting component creation...');
  const [subscription, setSubscription] = useState<SubscriptionState>(getInitialState);
  const upgradeModalTriggerRef = useRef<((feature?: FeatureKey, billingPeriod?: BillingPeriod) => void) | null>(null);
  const authContext = useAuth();
  const { user, userData, isLoading: authLoading } = authContext;
  
  console.log('🔍 SubscriptionProvider: Mounted with initial state:', subscription);
  console.log('🔍 SubscriptionProvider: Environment check:', {
    hasMetaEnv: !!import.meta.env,
    nodeEnv: typeof process !== 'undefined' ? process.env.NODE_ENV : 'unknown',
    viteMode: import.meta.env.MODE,
    viteProd: import.meta.env.PROD,
    viteDev: import.meta.env.DEV
  });
  console.log('🔍 SubscriptionProvider: Stripe key status:', {
    hasStripeKey: !!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
    keyLength: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY?.length || 0,
    keyPrefix: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY?.substring(0, 7) || 'none'
  });
  console.log('🔍 SubscriptionProvider: Dev premium check:', {
    hasLocalStorage: typeof window !== 'undefined',
    devPremium: getDevPremiumState()
  });

  // Initialize subscription state from Firebase user data
  useEffect(() => {
    console.log('🚀 SubscriptionProvider: useEffect triggered - Firebase auth state:', {
      hasUser: !!user,
      hasUserData: !!userData,
      authLoading,
      userPlan: userData?.plan
    });

    const initializeSubscription = async () => {
      console.log('⚡ SubscriptionProvider: initializeSubscription called');
      try {
        // Check for forced premium mode (allow in development and testing)
        const devPremium = getDevPremiumState();
        console.log('🛠️ SubscriptionProvider: Dev premium state:', devPremium);
        if (devPremium) {
          setSubscription({
            plan: 'premium',
            isPremium: true,
            isLoading: false,
            customerId: 'dev_customer',
            subscriptionId: 'dev_subscription'
          });
          return;
        }

        // If user is not authenticated, set to free plan
        if (!user) {
          console.log('👤 No authenticated user, setting free plan');
          setSubscription({
            plan: 'free',
            isPremium: false,
            isLoading: false
          });
          return;
        }

        // Use Firebase user data if available
        if (userData) {
          console.log('📊 Using Firebase user data:', userData.plan, 'stripeCustomerId:', userData.stripeCustomerId);
          setSubscription({
            plan: userData.plan,
            isPremium: userData.plan === 'premium',
            isLoading: false,
            customerId: userData.stripeCustomerId,
            subscriptionId: userData.subscriptionId,
            expiresAt: undefined // TODO: Add expiration handling if needed
          });
          return;
        }

        // Fallback: fetch from backend with Firebase token
        console.log('🌐 Fetching subscription from backend for user:', user.uid);
        const apiUrl = import.meta.env.DEV ? 'http://localhost:3001' : '';
        
        // Get Firebase ID token for authentication
        const token = await getAuthToken();
        if (!token) {
          console.warn('⚠️ No auth token available, using Firebase data');
          setSubscription({
            plan: (userData as any)?.plan || 'free',
            isPremium: ((userData as any)?.plan || 'free') === 'premium',
            isLoading: false,
            customerId: (userData as any)?.stripeCustomerId,
            subscriptionId: (userData as any)?.subscriptionId
          });
          return;
        }

        const response = await fetch(`${apiUrl}/api/user/subscription`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const subscriptionData = await response.json();
          console.log('✅ Backend subscription data:', subscriptionData);
          setSubscription({
            plan: subscriptionData.plan,
            isPremium: subscriptionData.isPremium,
            isLoading: false,
            customerId: subscriptionData.customerId,
            subscriptionId: subscriptionData.subscriptionId,
            expiresAt: subscriptionData.expiresAt ? new Date(subscriptionData.expiresAt * 1000) : undefined
          });
        } else {
          // Default to user's plan from Firebase or free
          console.log('⚠️ Backend unavailable, using Firebase data or free plan');
          setSubscription({
            plan: (userData as any)?.plan || 'free',
            isPremium: ((userData as any)?.plan || 'free') === 'premium',
            isLoading: false,
            customerId: (userData as any)?.stripeCustomerId,
            subscriptionId: (userData as any)?.subscriptionId
          });
        }
      } catch (error) {
        console.error('Failed to initialize subscription:', error);
        setSubscription({
          plan: (userData as any)?.plan || 'free',
          isPremium: ((userData as any)?.plan || 'free') === 'premium',
          isLoading: false,
          customerId: (userData as any)?.stripeCustomerId,
          subscriptionId: (userData as any)?.subscriptionId
        });
      }
    };

    // Only initialize when auth loading is complete
    if (!authLoading) {
      console.log('🔥 SubscriptionProvider: Auth loaded, initializing subscription...');
      initializeSubscription();
    }
  }, [user, userData, authLoading]);

  // Check if user has access to a feature
  const hasFeature = (feature: FeatureKey): boolean => {
    const featureConfig = FEATURES[feature];
    if (!featureConfig) {
      console.warn(`Unknown feature: ${feature}`);
      return false;
    }

    return featureConfig.plans.includes(subscription.plan);
  };

  // Check if feature requires premium
  const requiresPremium = (feature: FeatureKey): boolean => {
    const featureConfig = FEATURES[feature];
    if (!featureConfig) return false;
    
    return !featureConfig.plans.includes('free');
  };

  // Upgrade to premium (opens modal or Stripe checkout)
  const upgradeToPremium = async (triggerFeature?: FeatureKey, billingPeriod: BillingPeriod = 'monthly') => {
    console.log('🚀 upgradeToPremium called with feature:', triggerFeature, 'billing:', billingPeriod);
    console.log('🔍 upgradeModalTrigger available:', !!upgradeModalTriggerRef.current);
    
    // First try to use the modal trigger if available
    if (upgradeModalTriggerRef.current) {
      console.log('✅ Using modal trigger');
      upgradeModalTriggerRef.current(triggerFeature, billingPeriod);
      return;
    }
    
    console.log('⚠️ No modal trigger - falling back to alert');
    // Fallback to the old alert-based approach
    try {
      // Use relative URL in production, localhost in dev
      const apiUrl = import.meta.env.DEV ? 'http://localhost:3001' : '';
      
      // Check if backend is available first
      const healthCheck = await fetch(`${apiUrl}/api/health`).catch(() => null);
      
      if (!healthCheck || !healthCheck.ok) {
        // Backend not available - show development message
        alert(
          '🚀 Upgrade to Premium!\n\n' +
          'Premium features include:\n' +
          '• Cloud sync across devices\n' +
          '• Advanced export (PDF, MIDI)\n' +
          '• AI Jazz Fill & chart templates\n' +
          '• Setlist manager\n' +
          '• Advanced jam sessions\n\n' +
          'For now, use Development Mode in Subscription settings to test premium features.\n\n' +
          'To enable real payments, start the Stripe server with your API keys.'
        );
        return;
      }

      const stripe = await stripePromise;
      if (!stripe) {
        console.error('Stripe failed to load');
        return;
      }

      // Check if user is authenticated
      if (!user || !user.email) {
        alert('Please sign in first to upgrade to Premium!');
        return;
      }

      // Get Firebase ID token for secure API call
      const token = await getAuthToken();
      if (!token) {
        alert('Authentication error. Please sign in again.');
        return;
      }

      // Call your backend to create checkout session
      const response = await fetch(`${apiUrl}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email,
          embedded: false,
          billingPeriod: billingPeriod
        })
      });

      const { sessionId } = await response.json();

      // Redirect to Stripe checkout
      if (sessionId) {
        window.location.href = `https://checkout.stripe.com/pay/${sessionId}`;
      }
    } catch (error) {
      console.error('Failed to start checkout:', error);
      // Fallback message for any errors
      alert(
        '💫 Premium Features Available!\n\n' +
        'This feature requires a premium subscription.\n\n' +
        'Use Development Mode in Settings → Subscription to test premium features now!\n\n' +
        'For real payments, configure Stripe API keys in the backend.'
      );
    }
  };

  // Cancel subscription
  const cancelSubscription = async () => {
    try {
      // TODO: Call Stripe cancellation API
      console.log('Cancelling subscription...');
      
      setSubscription(prev => ({
        ...prev,
        plan: 'free',
        isPremium: false,
        subscriptionId: undefined
      }));
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };

  // Development helper to toggle premium - only in development mode
  const setDevMode = (isPremium: boolean) => {
    // Only allow in development mode for security
    if (import.meta.env.PROD) {
      console.warn('setDevMode only available in development');
      return;
    }

    localStorage.setItem('DEV_FORCE_PREMIUM', isPremium.toString());
    
    setSubscription(prev => ({
      ...prev,
      plan: isPremium ? 'premium' : 'free',
      isPremium,
      customerId: isPremium ? 'dev_customer' : undefined,
      subscriptionId: isPremium ? 'dev_subscription' : undefined
    }));
  };

  // Wrapper for setUpgradeModalTrigger with debugging
  const setModalTrigger = (trigger: ((feature?: FeatureKey) => void) | null) => {
    console.log('🎛️ setUpgradeModalTrigger called with:', !!trigger);
    upgradeModalTriggerRef.current = trigger;
  };

  const contextValue: SubscriptionContextType = {
    subscription,
    hasFeature,
    requiresPremium,
    upgradeToPremium,
    cancelSubscription,
    setDevMode,
    setUpgradeModalTrigger: setModalTrigger
  };

  console.log('🎯 SubscriptionProvider: About to render with context value:', {
    subscription,
    hasFeatureFunc: !!hasFeature,
    contextValue: Object.keys(contextValue)
  });

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Hook to use subscription context
export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Higher-order component for premium features
interface WithPremiumProps {
  feature: FeatureKey;
  children: ReactNode;
  fallback?: ReactNode;
  showUpgrade?: boolean;
}

export const WithPremium: React.FC<WithPremiumProps> = ({ 
  feature, 
  children, 
  fallback,
  showUpgrade = true 
}) => {
  const { hasFeature, upgradeToPremium, requiresPremium } = useSubscription();

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (showUpgrade && requiresPremium(feature)) {
    return (
      <button 
        onClick={(e) => { e.preventDefault(); upgradeToPremium(feature); }}
        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all"
      >
        🚀 Upgrade for {FEATURES[feature]?.name}
      </button>
    );
  }

  return null;
};

// Hook for feature access checking
export const useFeatureAccess = () => {
  const { hasFeature, requiresPremium, upgradeToPremium } = useSubscription();

  return {
    canUse: hasFeature,
    needsUpgrade: requiresPremium,
    upgrade: upgradeToPremium
  };
};